/**
 * WordPress dependencies
 */
import { store, getContext, getElement } from '@wordpress/interactivity';

const ROOT_SELECTOR = '.wp-block-best-anniversary-blocks-modal';
const DIALOG_SELECTOR = '.wp-block-best-anniversary-blocks-modal__dialog';
const BODY_SELECTOR = '.wp-block-best-anniversary-blocks-modal__body';

const FOCUSABLE_SELECTOR =
	'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

// ブロック単位の状態管理（複数インスタンス対応）
const instances = new Map();

function getFocusableElements( container ) {
	return [ ...container.querySelectorAll( FOCUSABLE_SELECTOR ) ].filter(
		( el ) => el.offsetParent !== null
	);
}

function setupFocusTrap( container, shouldFocusFirst ) {
	const trapHandler = ( event ) => {
		if ( event.key !== 'Tab' ) {
			return;
		}

		const focusableElements = getFocusableElements( container );
		if ( focusableElements.length === 0 ) {
			return;
		}

		const firstElement = focusableElements[ 0 ];
		const lastElement = focusableElements[ focusableElements.length - 1 ];
		const activeElement = container.ownerDocument.activeElement;

		if ( event.shiftKey && activeElement === firstElement ) {
			event.preventDefault();
			lastElement.focus();
		} else if ( ! event.shiftKey && activeElement === lastElement ) {
			event.preventDefault();
			firstElement.focus();
		}
	};

	container.addEventListener( 'keydown', trapHandler );

	if ( shouldFocusFirst ) {
		setTimeout( () => {
			const focusableElements = getFocusableElements( container );
			if ( focusableElements.length > 0 ) {
				focusableElements[ 0 ].focus( { preventScroll: true } );
			}
		}, 100 );
	}

	return () => container.removeEventListener( 'keydown', trapHandler );
}

/**
 * 閉じる時に中身のメディアを停止する。
 * - video/audio: 一時停止して先頭に戻す
 * - iframe(YouTube等): src を再代入して再生を止める
 * @param {HTMLElement} root モーダルのルート要素
 */
function stopMedia( root ) {
	const body = root.querySelector( BODY_SELECTOR );
	if ( ! body ) {
		return;
	}

	body.querySelectorAll( 'video, audio' ).forEach( ( media ) => {
		try {
			media.pause();
			media.currentTime = 0;
		} catch ( e ) {
			// 再生位置を戻せない場合は無視
		}
	} );

	body.querySelectorAll( 'iframe' ).forEach( ( frame ) => {
		const src = frame.getAttribute( 'src' );
		if ( src ) {
			frame.setAttribute( 'src', src );
		}
	} );
}

function doClose( root, returnFocus ) {
	const inst = instances.get( root );
	if ( ! inst ) {
		return;
	}

	inst.context.isOpen = false;
	if ( window.__lenis ) {
		window.__lenis.start();
	}

	if ( inst.cleanupFocusTrap ) {
		inst.cleanupFocusTrap();
	}

	stopMedia( root );

	if ( returnFocus && inst.triggerElement ) {
		inst.triggerElement.focus( { preventScroll: true } );
	}

	instances.delete( root );
}

store( 'best-anniversary-blocks/modal', {
	actions: {
		open( event ) {
			const { ref } = getElement();
			const root = ref.closest( ROOT_SELECTOR );
			if ( ! root ) {
				return;
			}

			// マウスクリック: detail > 0, キーボード: detail === 0
			const isKeyboard = event?.detail === 0;

			if ( window.__lenis ) {
				window.__lenis.stop();
			}

			const context = getContext();
			context.isOpen = true;

			const dialog = root.querySelector( DIALOG_SELECTOR );
			const cleanupFocusTrap = dialog
				? setupFocusTrap( dialog, isKeyboard )
				: null;

			instances.set( root, {
				context,
				triggerElement: ref,
				cleanupFocusTrap,
			} );
		},

		close() {
			const { ref } = getElement();
			const root = ref.closest( ROOT_SELECTOR );
			if ( root ) {
				doClose( root, true );
			}
		},

		// オーバーレイ(背景)の直接クリックだけ閉じる。ダイアログ内のクリックは無視。
		onOverlayClick( event ) {
			const { ref } = getElement();
			if ( event.target !== ref ) {
				return;
			}
			const root = ref.closest( ROOT_SELECTOR );
			if ( root ) {
				doClose( root, true );
			}
		},
	},
} );

// ESCキーで閉じる（フォーカスをトリガーに戻す）
document.addEventListener( 'keydown', ( event ) => {
	if ( event.key !== 'Escape' ) {
		return;
	}
	instances.forEach( ( _, root ) => doClose( root, true ) );
} );
